import React, {useEffect, useState} from "react";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "@mui/material/Button";
import FileUpload from "../../components/fileUpload";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import {useDispatch, useSelector} from "react-redux";
import {AppState} from "../../store";
import {TextFieldVariants} from "@mui/material/TextField/TextField";
import {AsyncTaskStatusType, FieldErrors} from "../../shared/dtos";
import ErrorText from "../../components/ErrorText";
import CustomDialog from "../../components/CustomDialog";
import {AddTaskFileDTO, AddTaskFileMetadata, TaskDTO} from "../../api/dtos";
import {addFileToTaskAction} from "../../api/task";
import {setAddFileToTaskStatus} from "../../features/task/taskSlice";

interface CreateTaskDialogProps {
    onClose: () => void;
    task: TaskDTO;
    onFileAdded: () => void;
    folderId?: number;
}

interface AddCreateTaskFormData {
    file: string | null;
    description: string | null;
}

const fieldVariant = "outlined"

export const AddFileToTaskDialog: React.FC<CreateTaskDialogProps> = ({
                                                                         onClose,
                                                                         task,
                                                                         folderId,
                                                                         onFileAdded
                                                                     }) => {
    const dispatch: any = useDispatch();

    const defaultFormData: AddCreateTaskFormData = {
        file: null,
        description: ''
    };

    const [formData, setFormData] = useState<AddCreateTaskFormData>(defaultFormData);

    const setTaskFileName = (fileName: string | null) => {
        setFormData({
            ...formData, file: fileName
        });
    }

    const handleChangeFormData = (event: any) => {
        const {name, value, type, checked} = event.target;

        if (type === 'checkbox') {
            setFormData({
                ...formData,
                [name]: checked
            });
        } else if (type === 'select-multiple') {
            setFormData({
                ...formData,
                // @ts-ignore
                [name]: Array.from(event.target.selectedOptions, (option) => option.value)
            });
        } else {
            let sideEffects = {};
            if (name === "cityId") {
                sideEffects = {
                    courtId: null,
                    courtHouseId: null
                };
            }
            if (name === "courtHouseId") {
                sideEffects = {
                    courtId: null
                };
            }
            setFormData({
                ...formData,
                [name]: value,
                ...sideEffects
            });
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const initialFormData = new FormData(event.currentTarget);

        const addFileTaskMeta: AddTaskFileMetadata = {
            file: initialFormData.get('file') as any,
            metadata: {
                description: formData.description,
                folderId: folderId || null
            } as AddTaskFileDTO
        };

        const submitFormData = new FormData();
        submitFormData.append("file", addFileTaskMeta.file);
        submitFormData.append("metadata", new Blob([JSON.stringify(addFileTaskMeta.metadata)], {type: "application/json"}))

        try {
            const result = await dispatch(addFileToTaskAction({
                taskId: task.id,
                dto: submitFormData
            }));
            if (result.type === "task/addFileToTask/fulfilled") {
                onFileAdded()
                onClose();
            }
        } catch (error) {
            console.error("Form gönderiminde bir hata oluştu:", error);
        }
    };

    const addFileToTaskStatus = useSelector((state: AppState) => state.task.addFileToTaskStatus);

    const [errors, setErrors] = useState<FieldErrors>({});

    useEffect(() => {
        if (addFileToTaskStatus?.fieldErrors) {
            const updatedErrors: FieldErrors = {};
            addFileToTaskStatus?.fieldErrors.forEach((it) =>
                updatedErrors[it.field] = it.message);
            setErrors(updatedErrors);
        }
    }, [addFileToTaskStatus]);

    useEffect(() => {
        return () => {
            dispatch(setAddFileToTaskStatus(null));
        };
    }, []);

    return (
        <CustomDialog
            open={true}
            onClose={onClose}
            PaperProps={{
                component: 'form',
                onSubmit: handleSubmit
            }}>
            <DialogTitle>Göreve Dosya Ekle ({task.title})</DialogTitle>
            <DialogContent>
                <FormControl fullWidth={true} sx={{mb: 1}}>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="description"
                        name="description"
                        label="Açıklama"
                        type="text"
                        fullWidth
                        variant={fieldVariant as TextFieldVariants}
                        onChange={handleChangeFormData}/>
                    {errors.name && (
                        <ErrorText message={errors.name}/>
                    )}
                </FormControl>
                <FormControl fullWidth={true} sx={{mb: 1}}>
                    <FileUpload
                        setFileName={setTaskFileName}
                        fieldName="file"
                        value={formData.file}
                        uploadButtonLabel="Evrak Ekle"/>
                    {errors.file && (
                        <ErrorText message={errors.file}/>
                    )}
                </FormControl>
                {addFileToTaskStatus?.type === AsyncTaskStatusType.Error && addFileToTaskStatus.errorMessage && (
                    <ErrorText type="form" message={addFileToTaskStatus.errorMessage}/>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>İptal</Button>
                <Button type="submit" disabled={!formData.file}>Ekle</Button>
            </DialogActions>
        </CustomDialog>
    );
}
